<template>
  <div class="workspace-groups">
    <title-panel
      :button-label="t('workspaceGroupsManagement.addGroup')"
      :count="workspaceGroups?.length ?? 0"
      :display-button="canManageWorkspaces"
      :on-button-click="onAddGroupButtonClick"
    >
      {{ t('workspaceGroupsManagement.groups') }}
    </title-panel>

    <add-workspace-group-modal
      ref="modalRef"
      :workspace-id="props.workspace._id"
      @group-created="fetchWorkspaceGroups"
    />

    <table-wrapper
      v-model:page-options="pageOptions"
      v-model:filters="filters"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="workspaceGroups ?? []"
      :loading="isLoading"
      :mode="TableMode.CLIENT_SIDE"
      row-clickable
      @row-click="handleRowClick"
    />
  </div>
</template>

<script lang="ts" setup>
  import { useRoles } from '@/composables/useRoles';
  import { useWorkspaceGroups } from '@/composables/api/workspaces/useWorkspaceGroups';
  import { useRouter } from 'vue-router';
  import { Route, routeParams } from '@/routes';
  import { WorkspaceGroupDetailViewTab } from '@/views/WorkspaceGroupDetailView/WorkspaceGroupDetailView.types';
  import AddWorkspaceGroupModal from '@/components/AddWorkspaceGroupModal/AddWorkspaceGroupModal.vue';
  import { IWorkspaceDetailsDto, IWorkspaceGroupSummaryDto } from 'types';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import {
    useTableWrapper,
    TableMode,
    TableFieldsBuilder,
    TableFiltersBuilder,
  } from '@/components/TableWrapper';

  const props = defineProps<{
    workspace: IWorkspaceDetailsDto;
  }>();

  const { t } = useI18n();
  const { canManageWorkspaces } = useRoles();
  const router = useRouter();

  const { workspaceGroups, fetchWorkspaceGroups } = useWorkspaceGroups(
    props.workspace._id,
  );

  const { pageOptions, filters, isLoading } =
    useTableWrapper<IWorkspaceGroupSummaryDto>(
      TableMode.CLIENT_SIDE,
      'name',
      fetchWorkspaceGroups,
      undefined,
      true,
    );

  const filterConfiguration =
    new TableFiltersBuilder<IWorkspaceGroupSummaryDto>({
      translationKey: 'workspaceGroupsManagement',
    })
      .addInputFilter('name', { width: '208px' })
      .build();

  const tableFields = computed(() =>
    new TableFieldsBuilder<IWorkspaceGroupSummaryDto>({
      sortable: true,
      labelTranslationKey: 'workspaceGroupsManagement',
    })
      .addField('name', { width: '70%' })
      .addField('usersCount', {
        width: '30%',
        label: t('workspaceGroupsManagement.usersCount'),
      })
      .build(),
  );

  const modalRef = ref<InstanceType<typeof AddWorkspaceGroupModal> | null>(
    null,
  );
  function onAddGroupButtonClick(): void {
    modalRef.value?.show();
  }

  function handleRowClick(group: IWorkspaceGroupSummaryDto): void {
    router.push({
      name: Route.WORKSPACE_GROUP_DETAILS,
      params: {
        [routeParams[Route.WORKSPACE_GROUP_DETAILS].workspaceId]:
          props.workspace._id,
        [routeParams[Route.WORKSPACE_GROUP_DETAILS].groupId]: group._id,
        [routeParams[Route.WORKSPACE_GROUP_DETAILS].tab]:
          WorkspaceGroupDetailViewTab.USERS,
      },
    });
  }
</script>

<style lang="scss" scoped>
  .title-panel {
    margin-top: $tab-view-margin-top;
  }
</style>
